import React, {useState, useEffect, useMemo} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Button,
  Badge,
  Form,
  Dropdown,
  Modal,
  Table,
} from "react-bootstrap";
import {
  FaEye,
  FaDownload,
  FaCheckCircle,
  FaPlus,
  FaUsers,
  FaBuilding,
  FaChartLine,
  FaFilter,
  FaSync,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaWhatsapp,
  FaRocket,
  FaComments,
  FaPlay,
  FaPause,
} from "react-icons/fa";
import {Link} from "react-router-dom";
import {FaUserPlus} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";
import {Line} from "react-chartjs-2";
import * as XLSX from "xlsx";
import RealTimeMetrics from "../components/RealTimeMetrics";

import {generateWhatsAppBusinessMetrics} from "../utils/whatsappBusinessMetrics";

// Registrar todos los componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Componente para KPI Cards mejorado
const AdvancedKPICard = ({
  icon,
  title,
  value,
  subtitle,
  trend,
  color = "primary",
  format = "number",
  target,
  onClick,
}) => {
  const getTrendIcon = () => {
    if (trend > 2) return <FaArrowUp className="text-success" />;
    if (trend < -2) return <FaArrowDown className="text-danger" />;
    return <FaMinus className="text-muted" />;
  };

  const formatValue = (val) => {
    switch (format) {
      case "percentage":
        return `${val}%`;
      case "currency":
        return `$${val?.toLocaleString() || 0}`;
      case "time":
        return val || "0";
      default:
        return val.toLocaleString();
    }
  };

  return (
    <Card
      className={`h-100 shadow-sm border-0 ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick}
      style={{cursor: onClick ? "pointer" : "default"}}
    >
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className={`text-${color}`} style={{fontSize: "2.5rem"}}>
            {icon}
          </div>
          {trend !== undefined && (
            <div className="d-flex align-items-center">
              {getTrendIcon()}
              <span
                className={`small ms-1 fw-bold ${
                  trend > 2
                    ? "text-success"
                    : trend < -2
                    ? "text-danger"
                    : "text-muted"
                }`}
              >
                {Math.abs(trend).toFixed(1)}%
              </span>
            </div>
          )}
        </div>

        <div className="mb-2">
          <div className="fw-bold" style={{fontSize: "2rem"}}>
            {formatValue(value)}
          </div>
          <div className="text-muted text-uppercase small fw-bold">{title}</div>
        </div>

        {subtitle && <div className="text-muted small">{subtitle}</div>}

        {target && (
          <div className="mt-2">
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span>Objetivo: {formatValue(target)}</span>
              <span>{((value / target) * 100).toFixed(0)}%</span>
            </div>
            <ProgressBar
              now={(value / target) * 100}
              style={{height: "4px"}}
              variant={
                value >= target
                  ? "success"
                  : value >= target * 0.8
                  ? "warning"
                  : "danger"
              }
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const Dashboard = ({
  campaigns = [],
  contacts = [],
  lists = [],
  incentives = [],
  organizations = [],
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState("30");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Generar datos realistas de WhatsApp Business
  const dashboardData = useMemo(
    () =>
      generateWhatsAppBusinessMetrics(
        campaigns,
        contacts,
        lists,
        organizations
      ),
    [campaigns, contacts, lists, organizations]
  );

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Configuración de gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.1)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  // Datos para gráfico de tendencia principal
  const trendChartData = {
    labels: dashboardData.dailyData
      .slice(-7)
      .map((d) =>
        new Date(d.date).toLocaleDateString("es-ES", {weekday: "short"})
      ),
    datasets: [
      {
        label: "Enviados",
        data: dashboardData.dailyData.slice(-7).map((d) => d.sent),
        borderColor: "rgb(34, 197, 94)",
        backgroundColor: "rgba(34, 197, 94, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Leídos",
        data: dashboardData.dailyData.slice(-7).map((d) => d.read),
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Respondidos",
        data: dashboardData.dailyData.slice(-7).map((d) => d.responded),
        borderColor: "rgb(249, 115, 22)",
        backgroundColor: "rgba(249, 115, 22, 0.1)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const handleExportDashboard = () => {
    const exportData = [
      {
        Métrica: "Campañas Totales",
        Valor: dashboardData.overview.totalCampaigns,
      },
      {
        Métrica: "Contactos Totales",
        Valor: dashboardData.overview.totalContacts,
      },
      {
        Métrica: "Tasa de Entrega",
        Valor: `${dashboardData.overview.deliveryRate}%`,
      },
      {
        Métrica: "Tasa de Lectura",
        Valor: `${dashboardData.overview.readRate}%`,
      },
      {
        Métrica: "Tasa de Respuesta",
        Valor: `${dashboardData.overview.responseRate}%`,
      },
      {Métrica: "ROI", Valor: `${dashboardData.overview.roi}%`},
      {Métrica: "Engagement", Valor: `${dashboardData.overview.engagement}%`},
      {
        Métrica: "Satisfacción",
        Valor: `${dashboardData.overview.satisfaction}/5`,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dashboard");
    XLSX.writeFile(
      workbook,
      `Dashboard_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  return (
    <Container fluid className="p-4 bg-light">
      {/* Header mejorado */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-bold mb-1">
                <FaChartLine className="me-2 text-primary" />
                Dashboard Ejecutivo
              </h2>
              <p className="text-muted mb-0">
                Visión completa de tu plataforma de comunicaciones
                <Badge bg="success" className="ms-2">
                  <FaSync className="me-1" />
                  Actualizado: {lastUpdate.toLocaleTimeString()}
                </Badge>
              </p>
            </div>
            <div className="d-flex gap-2">
              <Form.Select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                style={{width: "auto"}}
              >
                <option value="7">Últimos 7 días</option>
                <option value="30">Últimos 30 días</option>
                <option value="90">Últimos 3 meses</option>
              </Form.Select>

              <Button
                variant={autoRefresh ? "success" : "outline-secondary"}
                onClick={() => setAutoRefresh(!autoRefresh)}
                title="Auto-actualización"
              >
                {autoRefresh ? <FaPlay /> : <FaPause />}
              </Button>

              <Button variant="outline-primary" onClick={handleExportDashboard}>
                <FaDownload className="me-2" />
                Exportar
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* KPIs Principales - Fila 1 */}
      <Row className="g-4 mb-4">
        <Col xl>
          <AdvancedKPICard
            icon={<FaRocket />}
            title="Comunicaciones Activas"
            value={dashboardData.overview.activeCampaigns}
            subtitle={`${dashboardData.overview.totalCampaigns} comunicaciones totales`}
            trend={dashboardData.trends.campaigns}
            color="primary"
            target={20}
            onClick={() => {
              setSelectedMetric("campaigns");
              setShowDetailModal(true);
            }}
          />
        </Col>
        <Col xl>
          <AdvancedKPICard
            icon={<FaUsers />}
            title="Contactos Activos"
            value={dashboardData.overview.totalContacts}
            subtitle={`${dashboardData.overview.totalOrganizations} organizaciones`}
            trend={dashboardData.trends.contacts}
            color="info"
            target={1000}
          />
        </Col>
        <Col xl>
          <AdvancedKPICard
            icon={<FaCheckCircle />}
            title="Tasa de Entrega"
            value={dashboardData.overview.deliveredRate || 0}
            subtitle="WhatsApp Business API"
            trend={dashboardData.trends.deliveredRate}
            color="success"
            format="percentage"
            target={95}
          />
        </Col>
        <Col xl>
          <AdvancedKPICard
            icon={<FaWhatsapp />}
            title="Mensajes del Mes"
            value={dashboardData.overview.messagesThisMonth || 0}
            subtitle={`${dashboardData.overview.monthlySpend} gastados`}
            trend={dashboardData.trends.messageVolume}
            color="success"
            format="number"
            target={10000}
          />
        </Col>
        <Col xl>
          <AdvancedKPICard
            icon={<FaEye />}
            title="Tasa de Lectura"
            value={dashboardData.overview.readRate || 0}
            subtitle="Últimos 30 días"
            trend={dashboardData.trends.readRate}
            color="primary"
            format="percentage"
            target={85}
          />
        </Col>
      </Row>

      {/* Gráficos Principales */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0">
                <FaChartLine className="me-2 text-success" />
                Tendencia de Comunicaciones (7 días)
              </h6>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  <FaFilter className="me-1" />
                  Filtros
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item>Por canal</Dropdown.Item>
                  <Dropdown.Item>Por organización</Dropdown.Item>
                  <Dropdown.Item>Por tipo de mensaje</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Card.Header>
            <Card.Body style={{height: "400px"}}>
              <Line data={trendChartData} options={chartOptions} />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white">
              <h6 className="fw-bold mb-0">
                <FaBuilding className="me-2 text-primary" />
                Top Organizaciones
              </h6>
            </Card.Header>
            <Card.Body>
              {(dashboardData.topOrganizations || []).map((org, index) => (
                <div
                  key={org.id}
                  className="d-flex justify-content-between align-items-center mb-3"
                >
                  <div>
                    <div className="fw-bold small">{org.name}</div>
                    <div className="text-muted small">
                      {org.messages} mensajes • {org.responseRate}% respuesta
                    </div>
                  </div>
                  <div className="text-end">
                    <Badge bg="success">{org.engagement}%</Badge>
                    <div className="small text-muted">
                      {new Date(org.lastActivity).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Accesos Rápidos Mejorados */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white">
              <h6 className="fw-bold mb-0">
                <FaRocket className="me-2 text-primary" />
                Acciones Rápidas
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Button
                    as={Link}
                    to="/communications/new"
                    variant="primary"
                    className="w-100 mb-2"
                    size="lg"
                  >
                    <FaPlus className="me-2" />
                    Nueva Comunicación
                  </Button>
                </Col>
                <Col md={2}>
                  <Button
                    as={Link}
                    to="/contacts"
                    variant="outline-success"
                    className="w-100 mb-2"
                    size="lg"
                  >
                    <FaUserPlus className="me-2" />
                    Añadir Contacto
                  </Button>
                </Col>
                <Col md={2}>
                  <Button
                    as={Link}
                    to="/audiences"
                    variant="outline-info"
                    className="w-100 mb-2"
                    size="lg"
                  >
                    <FaUsers className="me-2" />
                    Crear Audiencia
                  </Button>
                </Col>
                <Col md={2}>
                  <Button
                    as={Link}
                    to="/analytics"
                    variant="outline-warning"
                    className="w-100 mb-2"
                    size="lg"
                  >
                    <FaChartLine className="me-2" />
                    Ver Analytics
                  </Button>
                </Col>
                <Col md={2}>
                  <Button
                    as={Link}
                    to="/inbox"
                    variant="outline-primary"
                    className="w-100 mb-2"
                    size="lg"
                  >
                    <FaComments className="me-2" />
                    Bandeja Entrada
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Métricas en Tiempo Real */}
      <Row>
        <Col>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white">
              <h6 className="fw-bold mb-0">
                <FaSync className="me-2 text-success" />
                Alertas del Sistema
              </h6>
            </Card.Header>
            <Card.Body>
              <RealTimeMetrics campaigns={campaigns} isLive={autoRefresh} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de Detalles */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Detalles de {selectedMetric}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMetric === "campaigns" && (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Comunicación</th>
                  <th>Estado</th>
                  <th>Enviados</th>
                  <th>Leídos</th>
                  <th>Respuestas</th>
                  <th>Tasa Éxito</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.recentCampaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="fw-bold">{campaign.name}</td>
                    <td>
                      <Badge
                        bg={
                          campaign.status === "Enviada"
                            ? "success"
                            : campaign.status === "Programada"
                            ? "primary"
                            : "secondary"
                        }
                      >
                        {campaign.status}
                      </Badge>
                    </td>
                    <td>{campaign.metrics.sent}</td>
                    <td>{campaign.metrics.read}</td>
                    <td>{campaign.metrics.responded}</td>
                    <td>{campaign.metrics.readRate}%</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Dashboard;
